import {NO_MATCH} from '@github-ui/fuzzy-score/fuzzy-score'
import type {Suggestion, Trigger} from '@github-ui/inline-autocomplete/types'

const MAX_SUGGESTIONS = 5

export type SuggestionOptions<T> = T[] | 'loading'

export type UseSuggestionsHook<T, C = void> = (
  options: SuggestionOptions<T>,
  config?: C,
) => {
  trigger: Trigger
  calculateSuggestions: (query: string) => Suggestion[] | 'loading'
}

export const suggestionsCalculator =
  <T>(
    options: SuggestionOptions<T>,
    score: (query: string, option: T) => number,
    tieBreaker: (optionOne: T, optionTwo: T) => number,
    toSuggestion: (option: T) => Suggestion,
    sortInitialOptions?: boolean,
  ) =>
  (query: string) => {
    if (options === 'loading') return 'loading'

    // If the query is empty or there is no match against the query, scores will be -Infinity
    const scoredAndSorted = query
      ? options
          .map(o => [score(query, o), o] as const)
          .filter(([s]) => s > NO_MATCH)
          .sort(([aScore, a], [bScore, b]) => {
            if (bScore === aScore) return -tieBreaker(b, a)
            return bScore - aScore
          })
          .slice(0, MAX_SUGGESTIONS)
          .map(([, o]) => o)
      : options.slice(0, MAX_SUGGESTIONS)

    if (sortInitialOptions) {
      scoredAndSorted.sort(tieBreaker)
    }

    return scoredAndSorted.map(toSuggestion)
  }
